<template>
  <div
    ref="container"
    class="facet-hero theme"
    :class="[
      model.themeCssClass && model.isFacetPage ? 'bg-gradient' : model.themeCssClass,
      { 'is-not-facet-page': !model.isFacetPage },
      { 'dark-overlay-enabled': model.enableDarkOverlay }
    ]"
    :style="{
      zIndex: isSticky ? 110 : 2
    }"
  >
    <div
      v-if="showImage"
      class="absolute top-0 left-0 h-full w-full overflow-hidden curve-bottom"
    >
      <transition
        name="facet-hero"
      >
        <Picture
          :key="imageSrc.url || imageSrc"
          img-class="absolute top-0 left-0 h-full w-full object-cover"
          :src="imageSrc"
          :image-resizer="imageResizer"
          :alt="alt"
          :full-width="true"
          :mobile="[375, 400]"
          :extra-wide="[1920, 600]"
          fetchpriority="high"
          width="1920"
          height="600"
          :options="{ mode: 'crop'}"
        />
      </transition>
    </div>
    <div
      class="facet-hero__text md:px-12 px-4 sm:pb-24 sm:py-24 py-16"
      :class="{
        'sm:pb-24': showImage,
        'md:py-16': !showImage
      }"
    >
      <div
        :class="{ 'curve-bottom': showImage }"
        class="facet-hero__text-overlay"
      />
      <div class="relative flex flex-col align-center w-full max-w-xl m-auto">
        <template v-if="!heroData">
          <div
            v-if="initialContent"
            v-html="initialContent"
          />
          <slot v-else />
        </template>
        <template v-else>
          <div class="text-center text-white">
            <h1 class="text-h1 flex flex-col mb-4 text-white font-bold">
              <span class="ltr:font-ltr rtl:font-rtl text-3xs font-normal mb-2 uppercase">
                {{ heroData.kicker }}
              </span>
              <span>{{ heroData.heading }}</span>
            </h1>
            <p
              v-if="heroData.preamble"
            >
              {{ heroData.preamble }}
            </p>
          </div>
        </template>
        <SearchBar
          class="mt-2"
          :model="model.searchBar"
          :is-facet-page="model.isFacetPage"
          :filters-button-text="model.filtersButtonText"
        />
        <transition-group
          class="flex flex-wrap items-center mt-6 justify-center gap-16px"
          :class="{
            'c-facet__recommended-tags': recommendedTags.length
          }"
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-300 ease-out"
          enter-class="opacity-0 invisible"
          enter-to-class="opacity-100 visible"
          leave-class="opacity-100 visible"
          leave-to-class="opacity-0 invisible"
        >
          <div
            v-for="(tag) in recommendedTags"
            :key="tag.id"
          >
            <button
              class="tag"
              @click="addTag(tag)"
            >
              {{ tag.name }}
              <IconArrowRight
                v-if="!model.isFacetPage && direction.dir === 'ltr'"
                class="fill-none text-blue-dark ltr:ml-2 rtl:mr-2"
                width="15px"
                height="10px"
              />
              <IconArrowLeft
                v-if="!model.isFacetPage && direction.dir === 'rtl'"
                class="fill-none text-blue-dark ltr:ml-2 rtl:mr-2"
                width="15px"
                height="10px"
              />
            </button>
          </div>
          <button
            key="filter"
            class="tag bg-blue-dark text-white"
            @click="model.isFacetPage ? openAllFilters() : goToOverviewPage()"
          >
            {{ model.filtersButtonText }}
            <IconPlus
              v-if="model.isFacetPage"
              class="fill-current ltr:ml-2 rtl:mr-2"
              width="10px"
              height="10px"
            />
            <IconArrowRight
              v-if="!model.isFacetPage && direction.dir === 'ltr'"
              class="fill-none text-white ltr:ml-2 rtl:mr-2"
              width="15px"
              height="10px"
            />
            <IconArrowLeft
              v-if="!model.isFacetPage && direction.dir === 'rtl'"
              class="fill-none text-white ltr:ml-2 rtl:mr-2"
              width="15px"
              height="10px"
            />
          </button>
        </transition-group>
      </div>
    </div>
    <div
      v-if="!model.isFacetPage && isPageRedirecting"
      class="absolute bottom-0 left-0 top-0 right-0 bg-white bg-opacity-50"
    >
      <div class="absolute top-1/2 left-1/2 w-16 h-16 transform -translate-x-1/2 -translate-y-1/2">
        <svg viewBox="0 0 50 50" class="new-spinner">
          <circle
            class="path"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke-width="5"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/components/facet/store'
import pageRefresh from '@/components/facet/page-refresh'
import Picture from '@/components/Picture.vue'
import SearchBar from '@/components/facet/SearchBar.vue'
import tracking from '@/helpers/tracking'
import IconPlus from '@/assets/icons/plus.svg'
import IconArrowRight from '@/assets/icons/arrow-right.svg'
import IconArrowLeft from '@/assets/icons/arrow-left.svg'
import isMobile from '@/lib/is-mobile'

export default {
  components: {
    IconPlus,
    IconArrowRight,
    IconArrowLeft,
    Picture,
    SearchBar
  },
  props: {
    slotContent: {
      type: String,
      default: ''
    },
    model: {
      type: Object,
      required: true
    },
    hideImage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      store,
      initialContent: this.slotContent,
      isSticky: false,
      breadcrumbs: document.querySelector('.js-facet-breadcrumbs ul'),
      languageSelector: document.querySelector('.js-language-selector')
    }
  },
  computed: {
    backgroundImage() {
      const { heroData } = this

      if (!heroData) {
        const { backgroundImage, defaultBackgroundImage } = this.model.data

        return backgroundImage || defaultBackgroundImage
      }

      const { backgroundImage, defaultBackgroundImage } = heroData

      return backgroundImage || defaultBackgroundImage
    },
    imageSrc() {
      return this.backgroundImage?.src
    },
    showImage() {
      return this.imageSrc && !this.hideImage
    },
    imageResizer() {
      return this.backgroundImage.imageResizer
    },
    heroData() {
      return this.store.state.heroData
    },
    isPageRedirecting() {
      return this.store.state.isNotFacetRedirecting
    },
    alt() {
      const { heroData } = this
      return heroData ? heroData.heading : this.model.data.heading
    },
    recommendedTags() {
      const { recommendedTags } = this.model
      const { smartTags, tags, isLayoutReady } = this.store.state

      if (!isLayoutReady && this.model.isFacetPage) {
        return []
      }

      if (tags.length) {
        return smartTags
      }

      return recommendedTags.filter(({ uid }) => !tags.find((tag) => tag.uid === uid))
    },
    direction() {
      const { dir } = window.document
      return { dir }
    }
  },
  watch: {
    'store.state.breadcrumbs': {
      handler(breadcrumbs) {
        this.updateBreacrumbHtml(breadcrumbs)
      }
    },
    'store.state.facetAltUrl': {
      handler(url) {
        this.updateLanguageSelectorUrl(url)
      }
    }
  },
  methods: {
    addTag(model) {
      const tag = { ...model }
      const addItemToStart = isMobile()
      const { currentTagsAndTerms: currentFilters } = this.store.getters
      tracking.selectFilter({
        currentFilters,
        filterName: tag.name,
        placement: 'hero filter'
      })
      if (!this.model.isFacetPage) {
        this.store.dispatch('setIsNotFacetPage')
        this.store.dispatch('setSortOrder', { sortOrder: 'Relevance' })
      }
      store.dispatch('addTag', { tag, addItemToStart })
      pageRefresh.updatePage()
    },
    openAllFilters() {
      const { currentTagsAndTerms: currentFilters } = this.store.getters
      tracking.openFilterMenu({
        currentFilters,
        placement: 'hero filter'
      })
      this.store.dispatch('setAllFiltersVisibility', { value: true })
    },
    goToOverviewPage() {
      window.location = window.basePageUrl
    },
    updateBreacrumbHtml(breadcrumbs) {
      const { dir } = window.document
      const html = breadcrumbs.map((item, index) => {
        const link = `<a href="${item.url}">${item.title}</a>`

        return `
        <li class="max-w-160 truncate">
          ${dir === 'ltr' && index != 0 ? '<span class="icon-chevron-right text-3xs ml-2 mr-1"></span>' : ''}
          ${index !== breadcrumbs.length - 1 ? link : item.title}
          ${dir === 'rtl' && index === breadcrumbs.length - 1 ? '<span class="icon-chevron-right text-3xs ml-2 mr-1"></span>' : ''}
        </li>
        `
      }).join(' ')
      if (this.breadcrumbs) {
        this.breadcrumbs.innerHTML = html
      }
    },
    updateLanguageSelectorUrl(url) {
      const { languageSelector } = this
      if (languageSelector && url) {
        languageSelector.href = url
      }
    }
  }
}
</script>

<style>
.facet-hero-enter-active, .facet-hero-leave-active {
  transition: opacity .2s;
}
.facet-hero-enter, .facet-hero-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

</style>
